import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { formatProductData } from '../helper/formatProductData';
import { useGlobal } from '../context/GlobalContext';
import { ProductGridSkeleton } from './SkeletonLoader';

const RecommendedOne = () => {
    const { categories: globalCategories, addToCart, cartItems, wishlistItems, addToWishlist, removeFromWishlist } = useGlobal();
    const [productsData, setProductsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState("All");
    const pillsRef = useRef(null);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setLoading(true);
                const response = await api.get('/api/products');
                const formatted = response.data.map(formatProductData).filter(p => p.isRecommended);
                setProductsData(formatted);
            } catch (err) {
                console.error("Failed to fetch recommended products", err);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, []);

    // Extract unique { id, label } categories dynamically from Global Context
    const categories = [
        { id: "All", label: "All" },
        ...globalCategories.map(c => ({ id: c.id, label: c.label || c.name })).slice(0, 5)
    ];

    const filteredProducts = activeCategory === "All"
        ? productsData
        : productsData.filter(product => product.categoryId === activeCategory);

    return (
        <section className="recommended pt-80">
            <div className="container container-lg">
                <div className="section-heading d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-16 mb-24">
                    <h5 className="mb-0 wow fadeInLeft flex-shrink-0" data-wow-duration="0.7s">Recommended for you</h5>
                    <style>{`
                        .recommended-pills-scroll {
                            display: flex !important;
                            flex-wrap: nowrap !important;
                            overflow-x: auto !important;
                            -webkit-overflow-scrolling: touch;
                            scrollbar-width: none;
                            -ms-overflow-style: none;
                            gap: 8px;
                            padding-bottom: 2px;
                            max-width: 100%;
                        }
                        .recommended-pills-scroll::-webkit-scrollbar {
                            display: none;
                        }
                        .recommended-pills-scroll .nav-link {
                            white-space: nowrap;
                            font-size: 0.875rem;
                            padding: 8px 16px;
                            border-radius: 999px;
                        }
                    `}</style>
                    <div className="w-100 w-md-auto d-flex align-items-center gap-8">
                        <ul
                            ref={pillsRef}
                            className="nav common-tab nav-pills recommended-pills-scroll wow fadeInRight m-0 flex-grow-1"
                            id="pills-tab"
                            role="tablist"
                            data-wow-duration="0.7s"
                        >
                            {categories.map((cat) => (
                                <li className="nav-item flex-shrink-0" role="presentation" key={cat.id}>
                                    <button
                                        className={`nav-link ${activeCategory === cat.id ? "active" : ""}`}
                                        onClick={() => setActiveCategory(cat.id)}
                                        type="button"
                                        role="tab"
                                        aria-selected={activeCategory === cat.id}
                                    >
                                        {cat.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                        <button
                            type="button"
                            onClick={() => pillsRef.current?.scrollBy({ left: 140, behavior: 'smooth' })}
                            className="d-flex d-md-none flex-center rounded-circle border border-gray-200 bg-white text-gray-700 shadow-xs flex-shrink-0"
                            style={{
                                width: 34,
                                height: 34,
                                cursor: 'pointer',
                            }}
                            title="Scroll categories"
                            aria-label="Scroll categories right"
                        >
                            <i className="ph-bold ph-caret-right text-xs text-neutral-800" />
                        </button>
                    </div>
                </div>
                <div className="tab-content" id="pills-tabContent">
                    <div
                        className="tab-pane fade show active"
                        role="tabpanel"
                        tabIndex={0}
                    >
                        <style>{`
                            @media (min-width: 992px) {
                                .col-custom-5 { width: 20%; flex: 0 0 auto; }
                            }
                            .pl-wishlist-btn {
                                position: absolute;
                                top: 10px;
                                left: 10px;
                                width: 30px;
                                height: 30px;
                                border-radius: 50%;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                z-index: 5;
                                transition: all 0.2s ease;
                                cursor: pointer;
                            }
                            .pl-wishlist-btn:hover {
                                transform: scale(1.1);
                            }
                            .product-card__cart {
                                white-space: nowrap;
                                z-index: 4;
                            }
                            @media (max-width: 576px) {
                                .pl-wishlist-btn {
                                    top: 6px;
                                    left: 6px;
                                    width: 26px;
                                    height: 26px;
                                }
                                .pl-wishlist-btn i {
                                    font-size: 13px;
                                }
                                .product-card__cart {
                                    font-size: 11px !important;
                                    padding: 3px 8px !important;
                                    margin-top: 6px !important;
                                    margin-right: 6px !important;
                                    gap: 4px !important;
                                }
                                .product-card__cart i {
                                    font-size: 12px !important;
                                }
                            }
                            @media (max-width: 380px) {
                                .product-card__cart {
                                    font-size: 10px !important;
                                    padding: 2px 6px !important;
                                    margin-top: 4px !important;
                                    margin-right: 4px !important;
                                    gap: 3px !important;
                                }
                            }
                        `}</style>
                        <div className="row g-12">
                            {loading ? (
                                <ProductGridSkeleton count={5} columnsClass="col-custom-5 col-md-3 col-sm-4 col-6" />
                            ) : filteredProducts.map((product, index) => {
                                const isInCart = cartItems?.some(item => String(item.id) === String(product.id));
                                const isWishlisted = wishlistItems?.some(item => String(item.id) === String(product.id));
                                return (
                                <div className="col-custom-5 col-md-3 col-sm-4 col-6" key={product.id} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                                    <div className="product-card hover-lift p-12 border border-gray-100 hover-border-main-600 rounded-16 position-relative transition-2 d-flex flex-column" style={{ height: '100%', width: '100%' }}>
                                        {/* Wishlist Button */}
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (isWishlisted) {
                                                    removeFromWishlist(product.id);
                                                } else {
                                                    addToWishlist(product);
                                                }
                                            }}
                                            className={`pl-wishlist-btn border ${
                                                isWishlisted
                                                    ? 'bg-danger-50 border-danger-200 text-danger-600'
                                                    : 'bg-white border-gray-100 text-gray-400 hover-text-main-600 hover-border-main-600 shadow-xs'
                                            }`}
                                            title={isWishlisted ? "In Wishlist (Click to remove)" : "Add to Wishlist"}
                                            aria-label="Wishlist"
                                        >
                                            <i className={`${isWishlisted ? 'ph-fill ph-heart text-danger-600' : 'ph ph-heart'} text-sm`} />
                                        </button>
                                        {/* 1. Photo */}
                                        <Link
                                            to={`/product-details/${product.slug || product.id}`}
                                            state={{ id: product.slug || product.id }}
                                            className="product-card__thumb flex-center"
                                            style={{ height: '130px', width: '100%', padding: '4px' }}
                                        >
                                            <img
                                                src={product.image}
                                                alt={product.name}
                                                style={{ width: 'auto', height: '100%', maxWidth: '100%', maxHeight: '120px', objectFit: 'contain' }}
                                            />
                                        </Link>

                                        {/* 2. Middle: Add to Cart / View Cart Button */}
                                        <div className="product-card__action my-8 w-100">
                                            {isInCart ? (
                                                <Link
                                                    to="/cart"
                                                    className="product-card__cart btn bg-main-600 text-white hover-bg-main-700 py-8 px-14 text-xs rounded-pill flex-center gap-6 w-100"
                                                >
                                                    View Cart <i className="ph ph-shopping-cart" />
                                                </Link>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.preventDefault(); addToCart(product, 1, (product.rawVariants && product.rawVariants.length > 0) ? product.rawVariants[0] : null); }}
                                                    className="product-card__cart btn bg-main-50 text-main-600 hover-bg-main-600 hover-text-white py-8 px-14 text-xs rounded-pill flex-center gap-6 w-100"
                                                >
                                                    Add to Cart <i className="ph ph-shopping-cart" />
                                                </button>
                                            )}
                                        </div>

                                        {/* 3. Text details (Price & Name) */}
                                        <div className="product-card__content mt-4 flex-shrink-0 d-flex flex-column" style={{ flex: '1 1 auto' }}>
                                            <div className="product-card__price mb-4 d-flex align-items-center gap-8">
                                                <span className="text-heading text-sm fw-semibold">
                                                    {product.currentPrice} <span className="text-gray-500 fw-normal text-xs">{product.quantityText}</span>
                                                </span>
                                                <span className="text-gray-400 text-sm fw-semibold text-decoration-line-through">
                                                    {product.originalPrice}
                                                </span>
                                            </div>

                                            <h6 className="title text-sm fw-semibold mb-0" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.4', height: '2.8em' }}>
                                                <Link to={`/product-details/${product.slug || product.id}`} state={{ id: product.slug || product.id }} className="link text-line-2">
                                                    {product.name}
                                                </Link>
                                            </h6>
                                        </div>
                                    </div>
                                </div>
                            );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default RecommendedOne
